import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSetAtom, useAtomValue } from "jotai";
import { userAtom, setAuthAtom } from "../state/atoms/authAtoms";
import authService from "../services/auth/authService";
import { AccountType, Role, UserType } from "../types/enums";

interface ProtectedRouteProps {
  allowedRoles?: (Role | null)[];
  allowedTypes: UserType[];
  allowedAccounts?: (AccountType | null)[];
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  allowedTypes,
  allowedAccounts,
  children,
}) => {
  const user = useAtomValue(userAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = () => {
      const token = localStorage.getItem("authToken");

      if (token) {
        try {
          const decodedUser = authService.decodeToken(token);
          if (decodedUser.exp*1000 < Date.now()) {
            throw new Error("Token expired");
          }
          setAuth({ user: decodedUser, token });
        } catch (error) {
          console.error("Invalid or expired token:", error);
          localStorage.removeItem("authToken");
          setAuth({ user: null, token: null });
        }
      }
      setLoading(false);
    };

    if (!user) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, [user, setAuth]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user.role ? user.role.toUpperCase() : null;
  const userType = user.user_type?.toUpperCase();
  const userAccountType = user.account_type ? user.account_type.toUpperCase() : null;

  const isAccountValid = userType === UserType.PERSONAL ? true : allowedAccounts?.includes(userAccountType as AccountType);
  const isRoleValid = userType === UserType.PERSONAL ? true : allowedRoles?.includes(userRole as Role | null);

  if (
    !allowedTypes.includes(userType as UserType) ||
    !isRoleValid ||
    !isAccountValid
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
